import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useStore } from 'zustand';
import { moFamilyStore } from '../stores/moFamilyStore';
import { MoFamilySlot } from '../types';
import { bindSlot as bindBoxToSlotApi, listBindings, unbindSlot } from '../utils/videobox';
import { isExpired, eventDDay } from '../utils/datetime';

interface SlotDisplayProps {
 familyHash: string;
 dropEnabled?: boolean;
 onDragStart?: () => void;
 onDragEnd?: () => void;
 currentTopFamilyHash?: string;
 onFamilyVideosChanged?: () => void;
}

// Export removeVideoFromSlot function
export const removeVideoFromSlot = async (familyHash: string, slotNumber: number, editingInactive?: boolean, dropEnabled: boolean = true) => {
 try {
 const bodyData: any = { file_hash: null }; // assign API에 null을 보내서 슬롯을 비움
 const store = moFamilyStore.getState();
 
 // Multi-slot system support
 // For child families (when dropEnabled is false), don't send set_name
 // This will make the server use the active set
 if (dropEnabled && store.editingSlotSet) {
 bodyData.set_name = store.editingSlotSet;
 } else if (editingInactive) {
 // Legacy dual-slot support
 bodyData.editing_set = 'inactive';
 }
 
 const response = await fetch(`/mofamily/${familyHash}/slot/${slotNumber}/assign`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json'
 },
 credentials: 'include',
 body: JSON.stringify(bodyData)
 });
 
 if (!response.ok) {
 const error = await response.json();
 throw new Error(error.error || 'Failed to remove video from slot');
 }

 // ⚠️ 박스 구동 슬롯을 비우면 서버가 바인딩도 함께 지운다(assign-null 통일, 2026-08-23).
 //    이 함수는 컴포넌트 밖 모듈 함수라 loadBindings 를 직접 못 부른다 — 이벤트로 통지해
 //    보라 박스 태그가 즉시 사라지게 한다(안 하면 DB 는 지워졌는데 태그만 남는 유령 상태).
 window.dispatchEvent(new CustomEvent('moc:bindings-changed'));

 // Get the updated slots from the response
 const responseData = await response.json();
 const updatedSlotsFromServer = responseData.data;
 
 // Update the store with the new slots for multi-slot system
 const currentSets = store.slotSets.get(familyHash) || [];
 const targetSetName = dropEnabled ? (store.editingSlotSet || 'A') : 'active';
 
 const updatedSets = currentSets.map(set => {
 // For child families, update the active set
 // For current family, update the editing set
 if ((targetSetName === 'active' && set.isActive) || 
 (targetSetName !== 'active' && set.setName === targetSetName)) {
 return { ...set, slots: updatedSlotsFromServer };
 }
 return set;
 });
 
 store.setSlotSets(familyHash, updatedSets);
 
 return true;
 } catch (error) {
 console.error('Error removing video from slot:', error);
 throw error;
 }
};

export const SlotDisplay: React.FC<SlotDisplayProps> = ({ familyHash, dropEnabled = false, onDragStart, onDragEnd, currentTopFamilyHash, onFamilyVideosChanged }) => {
 const [slots, setSlots] = useState<MoFamilySlot[]>([]);

 // Zustand useStore로 슬롯 데이터 직접 구독 — 변경 시 자동 리렌더
 const editingSlotSet = useStore(moFamilyStore, (state) => state.editingSlotSet);
 const storeSlotSets = useStore(moFamilyStore, (state) => state.slotSets.get(familyHash));
 const storeSlots = useMemo(() => {
  if (!storeSlotSets || storeSlotSets.length === 0) return null;
  const editingSetName = dropEnabled ? (editingSlotSet || 'A') : 'active';
  const targetSet = editingSetName === 'active'
   ? storeSlotSets.find(s => s.isActive) || storeSlotSets[0]
   : storeSlotSets.find(s => s.setName === editingSetName) || storeSlotSets.find(s => s.isActive) || storeSlotSets[0];
  return Array.isArray(targetSet?.slots) ? targetSet.slots : null;
 }, [storeSlotSets, editingSlotSet, dropEnabled]);

 // storeSlots가 있으면 항상 우선 사용 (동기화 후 즉시 반영)
 const effectiveSlots = storeSlots || slots;
 const [family, setFamily] = useState<any>(null);
 const [slotDelegationInfo, setSlotDelegationInfo] = useState<{ [key: number]: 'dedicated' | 'shared' | null }>({});

 // 영상 박스로 구동 중인 슬롯 번호 집합(오버레이 뱃지용). 박스 소유=peer(107) 이후
 // 상대 패밀리의 위임 슬롯 배치도 뱃지가 보여야 하므로 모든 행에서 로드한다(서버 게이트
 // = 위임 체인 가시성). 표시 세트: 편집 중이면 편집 셋, 아니면 활성 셋(모르면 무필터).
 const [boxBoundSlots, setBoxBoundSlots] = useState<Map<number, { name: string; type: 'schedule' | 'sequence' | 'group'; setName: string }>>(new Map());
 const loadBindings = useCallback(async () => {
 try {
 const sets = moFamilyStore.getState().slotSets.get(familyHash);
 const setName = dropEnabled
 ? (moFamilyStore.getState().editingSlotSet || 'A')
 : (sets?.find(s => s.isActive)?.setName ?? null);
 const bs = await listBindings(familyHash);
 setBoxBoundSlots(new Map(bs.filter(b => setName === null || b.set_name === setName)
 .map(b => [b.slot_number, { name: b.box_name, type: b.box_type, setName: b.set_name }])));
 } catch { /* 오버레이 실패는 무시(권한 밖 family 는 403) */ }
 }, [dropEnabled, familyHash]);

 // 슬롯 롤오버에 영상 제목을 보여주기 위한 hash→title 맵 (이 패밀리의 영상 목록).
 // ⚠️ /mofile/family 는 멤버 전용 — 상대(위임 체인) family 행에서 호출하면 403 콘솔 소음만
 //    남는다(툴팁 스펙도 위임 슬롯 제외). 내가 운영하는 family 일 때만 요청한다.
 const famInfo = useStore(moFamilyStore, (s) => s.families.get(familyHash));
 const canReadFamilyVideos = dropEnabled || famInfo?.isOwner
 || famInfo?.role === 'owner' || famInfo?.role === 'operator';
 const [videoTitles, setVideoTitles] = useState<Map<string, string>>(new Map());
 useEffect(() => {
 if (!canReadFamilyVideos) return;
 let alive = true;
 void (async () => {
 try {
 const r = await fetch(`/mofile/family/${familyHash}`, { credentials: 'include' });
 if (!r.ok) return;
 const d = await r.json();
 if (alive) setVideoTitles(new Map((d.data ?? []).map((v: { file_hash: string; title: string }) => [v.file_hash, v.title])));
 } catch { /* 제목은 장식 — 실패 무시 */ }
 })();
 return () => { alive = false; };
 }, [familyHash, canReadFamilyVideos]);
 // 마운트 + 세트 전환(effectiveSlots 변경) 시 갱신.
 useEffect(() => { void loadBindings(); }, [loadBindings, editingSlotSet]);
 // removeVideoFromSlot(모듈 함수)이 바인딩을 지웠을 때의 통지 — 태그 오버레이 즉시 갱신.
 useEffect(() => {
 const onChanged = () => { void loadBindings(); };
 window.addEventListener('moc:bindings-changed', onChanged);
 return () => window.removeEventListener('moc:bindings-changed', onChanged);
 }, [loadBindings]);

 // Add CSS for dragging effect
 useEffect(() => {
 const style = document.createElement('style');
 style.textContent = `
 .dragging {
 opacity: 0.5 !important;
 }
 @media screen and (-webkit-min-device-pixel-ratio:0) {
 /* Chrome/Safari specific drag image opacity */
 .dragging {
 -webkit-filter: opacity(0.6);
 filter: opacity(0.6);
 }
 }
 /* 내가 독점 위임해서 사용할 수 없는 슬롯 — 검은 사선 빗금 ('내가 넘긴 것') */
 .slot-delegated-away {
 background-image: repeating-linear-gradient(45deg, rgba(0,0,0,0.12) 0, rgba(0,0,0,0.12) 3px, transparent 3px, transparent 7px);
 }
 /* 원래 내 권한이 아닌(남의) 슬롯 — 같은 '제어 불가'이지만 색을 달리해 위임-넘김과 구분 (블루그레이 사선) */
 .slot-no-authority {
 background-image: repeating-linear-gradient(45deg, rgba(96,125,139,0.18) 0, rgba(96,125,139,0.18) 3px, transparent 3px, transparent 7px);
 }
 `;
 document.head.appendChild(style);
 
 return () => {
 document.head.removeChild(style);
 };
 }, []);
 
 useEffect(() => {
 const store = moFamilyStore.getState();
 const familyData = store.getFamilyByHash(familyHash);
 setFamily(familyData);
 
 const loadSlots = async () => {
 // Get the latest editingSlotSet from store
 const currentStore = moFamilyStore.getState();
 
 // For child families (when dropEnabled is false), always use the active set
 // For current family (when dropEnabled is true), use the editing set
 const editingSetName = dropEnabled ? (currentStore.editingSlotSet || 'A') : 'active';
 
 // Load slots from the multi-slot system
 try {
 // First check if we have cached slot sets
 const cachedSets = currentStore.slotSets.get(familyHash);
 if (cachedSets && cachedSets.length > 0) {
 const targetSet = editingSetName === 'active' 
 ? cachedSets.find(s => s.isActive) || cachedSets[0]
 : cachedSets.find(s => s.setName === editingSetName) || 
 cachedSets.find(s => s.isActive) || 
 cachedSets[0];
 const validSlots = Array.isArray(targetSet.slots) ? targetSet.slots : [];
 setSlots(validSlots);
 } else {
 // Load slot sets from API
 const response = await fetch(`/mofamily/${familyHash}/slot-sets`, {
 credentials: 'include'
 });
 
 if (response.ok) {
 const data = await response.json();
 const sets = data.data.sets;
 currentStore.setSlotSets(familyHash, sets);
 
 // For 'active', find the active set; otherwise find by name
 const targetSet = editingSetName === 'active'
 ? sets.find(s => s.isActive) || sets[0]
 : sets.find(s => s.setName === editingSetName) || 
 sets.find(s => s.isActive) || 
 sets[0];
 const validSlots = Array.isArray(targetSet?.slots) ? targetSet.slots : [];
 setSlots(validSlots);
 } else {
 console.error('Failed to load slot sets:', response.status);
 throw new Error(`Multi-slot API not available: ${response.status}`);
 }
 }
 } catch (error) {
 console.error('Error loading slots:', error);
 setSlots([]);
 }
 };
 
 loadSlots();
 
 // Load delegation info from hierarchy/relationships
 const loadDelegationInfo = async () => {
 // Only load delegation info when viewing own slots (dropEnabled = true)
 if (dropEnabled) {
 try {
 const response = await fetch(`/mofamily/${familyHash}/delegation-info`, {
 credentials: 'include'
 });
 if (response.ok) {
 const data = await response.json();
 // Convert delegation data to slot number mapping
 const delegationMap: { [key: number]: 'dedicated' | 'shared' | null } = {};
 if (data.data) {
 data.data.dedicatedSlots?.forEach((slotNum: number) => {
 delegationMap[slotNum] = 'dedicated';
 });
 data.data.sharedSlots?.forEach((slotNum: number) => {
 delegationMap[slotNum] = 'shared';
 });
 }
 setSlotDelegationInfo(delegationMap);
 }
 } catch (error) {
 }
 }
 };
 
 loadDelegationInfo();
 
 // Subscribe to family info changes (slots are handled by useStore above)
 const unsubscribe = moFamilyStore.subscribe((state) => {
 if (dropEnabled) {
 const newFamily = state.getFamilyByHash(familyHash);
 if (newFamily) {
 setFamily(newFamily);
 }
 }
 });
 
 return () => unsubscribe();
 }, [familyHash, dropEnabled, editingSlotSet, currentTopFamilyHash]);
 
 const totalSlots = effectiveSlots?.length || family?.totalSlots || 0;

 // Ensure slots is always an array
 const slotsArray = Array.isArray(effectiveSlots) ? effectiveSlots : [];
 
 // Helper function to get slot border color based on delegation type
 const getSlotBorderColor = (delegationType: string) => {
 switch (delegationType) {
 case 'dedicated':
 return 'border-blue-500 border-2';
 case 'shared':
 return 'border-green-500 border-2';
 default:
 return 'border-gray-300';
 }
 };
 
 // Helper function to get slot tooltip
 const getSlotTooltip = (delegationType: string, slot: any, hasAuthority: boolean) => {
 // 첫 줄 = 슬롯 내용 이름(박스면 박스명·타입, 영상이면 제목) — 롤오버로 무엇인지 보이게.
 const bound = boxBoundSlots.get(slot?.slot_number);
 const contentLine = bound
 ? `${bound.type === 'sequence' ? '🔁' : bound.type === 'group' ? '🗂️' : '📦'} ${bound.name} (${bound.type === 'sequence' ? '시퀀싱' : bound.type === 'group' ? '그룹' : '스케줄'} 박스)`
 : slot?.file_hash
 ? `📹 ${videoTitles.get(slot.file_hash) ?? String(slot.file_hash).slice(5, 13)}`
 : '';
 const withContent = (rest: string) => (contentLine ? `${contentLine}\n${rest}` : rest);
 // 내 슬롯을 보는 중인데 권한이 없다 = 독점으로 위임해 사용권이 넘어간 슬롯
 // (공유 위임은 권한이 유지되므로 여기 안 걸림).
 // 위임 슬롯은 내용 이름을 붙이지 않는다(사용자 확정 2026-08-23) — 남의 편성이다.
 if (dropEnabled && !hasAuthority) {
 return '독점 위임되어 내가 사용할 수 없는 슬롯입니다\n(드래그해 다른 슬롯과 재생 순서를 바꿀 수 있습니다)';
 }
 if (!dropEnabled && !hasAuthority) {
 return '이 슬롯에 대한 권한이 없습니다';
 }

 if (delegationType === 'dedicated') {
 return '독점 위임된 슬롯';
 } else if (delegationType === 'shared') {
 return '공유 위임된 슬롯';
 }

 return withContent(`슬롯 ${slot?.slot_number || ''}`);
 };
 
 // Drag and drop handlers — 내 영상 / 사용자 영상 / 슬롯-to-슬롯 만 처리.
 // file_shares 자동 생성(autoShareToFamily) 흐름과 'application/x-family-video' 분기는
 // 다운로드 정책이 slot_sets union 으로 변경되면서 (2026-04-27) 더 이상 필요 없음.
 const showSlotToast = (msg: string, kind: 'ok' | 'fail') => {
 const n = document.createElement('div');
 n.className = `fixed top-4 right-4 ${kind === 'ok' ? 'bg-green-500' : 'bg-red-500'} text-white px-4 py-2 shadow-lg z-50`;
 n.textContent = msg;
 document.body.appendChild(n);
 setTimeout(() => n.remove(), kind === 'ok' ? 2000 : 4000);
 };

 const handleDrop = async (e: React.DragEvent<HTMLDivElement>, slotNumber: number, contentDropAllowed: boolean = true) => {
 e.preventDefault();
 e.stopPropagation();

 const target = e.currentTarget;
 target.style.borderColor = '';
 target.style.borderWidth = '';
 target.style.backgroundColor = '';

 const types = Array.from(e.dataTransfer.types);
 const slotVideoData = e.dataTransfer.getData('application/x-slot-video');
 const videoData = e.dataTransfer.getData('application/x-video-drag');
 const boxData = e.dataTransfer.getData('application/x-videobox-drag');
 const fileData = e.dataTransfer.getData('application/json');
 console.log('[SlotDisplay] drop slot=%d types=%o slotVideo=%s videoDrag=%s json=%s',
 slotNumber, types,
 slotVideoData ? 'yes' : 'no',
 videoData ? 'yes' : 'no',
 fileData ? 'yes' : 'no');

 try {
 // 영상 박스 뱃지를 드래그해 슬롯 위로 드롭 → 원본 슬롯에서 박스 unbind.
 const unbindData = e.dataTransfer.getData('application/x-videobox-unbind');
 if (unbindData) {
 const { slotNumber: srcSlot } = JSON.parse(unbindData);
 await handleUnbindBox(srcSlot);
 return;
 }

 // 슬롯 순서 스왑 — 이동 핸들(또는 위임 슬롯 자체)을 다른 슬롯 위에 드롭.
 // 위임·바인딩·라벨·내용이 번호를 따라 함께 이동한다(서버 swapSlotNumbers).
 const swapData = e.dataTransfer.getData('application/x-slot-swap');
 if (swapData) {
 const { slotNumber: srcSlot } = JSON.parse(swapData);
 if (srcSlot === slotNumber) return;
 const r = await fetch(`/mofamily/${familyHash}/slots/swap`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ slotA: srcSlot, slotB: slotNumber }),
 });
 if (!r.ok) {
 const j = await r.json().catch(() => ({}));
 throw new Error(j.error || '슬롯 순서 변경에 실패했습니다');
 }
 await reloadSlotSets();
 await loadBindings();
 // 위임 테두리(파랑/초록)도 번호를 따라 이동 — 관계 기반 delegation-info 재조회.
 try {
 const dr = await fetch(`/mofamily/${familyHash}/delegation-info`, { credentials: 'include' });
 if (dr.ok) {
 const dd = await dr.json();
 const delegationMap: { [key: number]: 'dedicated' | 'shared' | null } = {};
 dd.data?.dedicatedSlots?.forEach((n: number) => { delegationMap[n] = 'dedicated'; });
 dd.data?.sharedSlots?.forEach((n: number) => { delegationMap[n] = 'shared'; });
 setSlotDelegationInfo(delegationMap);
 }
 } catch { /* 테두리는 장식 — 실패 무시 */ }
 showSlotToast(`슬롯 ${srcSlot} ↔ ${slotNumber} 순서를 바꿨습니다`, 'ok');
 onFamilyVideosChanged?.();
 return;
 }

 // 스왑 외 콘텐츠 드롭은 기존 규칙(권한 있고 빈 슬롯)대로만 받는다.
 if (!contentDropAllowed) return;

 // Slot-to-slot drag
 if (slotVideoData) {
 const sourceData = JSON.parse(slotVideoData);
 await assignVideoToSlot(slotNumber, sourceData.fileHash);
 if (sourceData.slotNumber !== slotNumber && sourceData.familyHash === familyHash) {
 await removeVideoFromSlot(familyHash, sourceData.slotNumber, true, dropEnabled);
 // assign 은 서버 응답(원본 슬롯이 아직 찬 상태)으로 slotSets 를 갱신하고,
 // 이어지는 remove 는 state.slots(렌더가 안 읽는 슬라이스)만 건드려 원본 슬롯이
 // 화면에서 안 지워졌다. 두 서버 반영 후 권위 상태를 다시 읽어 slotSets 동기화.
 await reloadSlotSets();
 }
 showSlotToast(`슬롯 ${slotNumber} 에 할당`, 'ok');
 return;
 }

 // 내 영상 (MoFile.tsx 의 file 카드) → 슬롯
 if (fileData) {
 const parsed = JSON.parse(fileData);
 const fileHash = parsed.fileHash || parsed.file_hash;
 if (!fileHash) {
 console.warn('[SlotDisplay] json drop: no fileHash field', parsed);
 showSlotToast('드래그 데이터에 fileHash 가 없습니다', 'fail');
 return;
 }
 await assignVideoToSlot(slotNumber, fileHash);
 showSlotToast(`슬롯 ${slotNumber} 에 할당`, 'ok');
 return;
 }

 // 영상 박스 → 슬롯 (bind). 박스 소유=peer(107) — 배치 가능 여부는 서버의 위임 규칙
 // (resolvePlacementAuthority)이 판정한다. 자기(편집) 패밀리면 편집 셋 명시, 남의
 // 패밀리면 setName 을 보내지 않아 서버가 활성 셋으로 해석(팬텀 세트 차단).
 if (boxData) {
 const { boxId } = JSON.parse(boxData);
 const setName = dropEnabled ? (moFamilyStore.getState().editingSlotSet || 'A') : undefined;
 await bindBoxToSlotApi(boxId, familyHash, slotNumber, setName);
 await reloadSlotSets();
 await loadBindings();
 showSlotToast(`슬롯 ${slotNumber} 에 영상 박스를 넣었습니다`, 'ok');
 onFamilyVideosChanged?.();
 return;
 }

 // 사용자 영상 목록 → 슬롯
 if (videoData) {
 const data = JSON.parse(videoData);
 await assignVideoToSlot(slotNumber, data.fileHash);
 showSlotToast(`슬롯 ${slotNumber} 에 할당`, 'ok');
 return;
 }

 console.warn('[SlotDisplay] drop: no recognized data type. types=%o', types);
 showSlotToast(`드롭 실패 — 인식되지 않는 데이터 (types: ${types.join(', ') || 'none'})`, 'fail');
 } catch (error: any) {
 console.error('[SlotDisplay] drop error:', error);
 showSlotToast(error.message || '영상 할당 실패', 'fail');
 }
 };

 // 영상 박스 뱃지를 슬롯 밖으로 드래그 → 해당 슬롯에서 박스 unbind(슬롯도 비워짐).
 const handleUnbindBox = async (slotNumber: number) => {
 // 세트명은 바인딩 행의 것을 쓴다 — 남의 family 에서 전역 편집 셋 이름을 쓰면 어긋난다.
 const setName = boxBoundSlots.get(slotNumber)?.setName
 ?? (moFamilyStore.getState().editingSlotSet || 'A');
 try {
 await unbindSlot(familyHash, setName, slotNumber);
 await reloadSlotSets();
 await loadBindings();
 showSlotToast(`슬롯 ${slotNumber} 에서 영상 박스를 뺐습니다`, 'ok');
 onFamilyVideosChanged?.();
 } catch (e: any) {
 showSlotToast(e?.message || '영상 박스 빼기 실패', 'fail');
 }
 };

 // dragover 는 매 프레임 호출되며 preventDefault 가 drop 을 허용한다.
 // 핵심은 preventDefault — 누락되면 drop event 자체가 발생하지 않음.
 const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
 e.preventDefault();
 e.stopPropagation();

 const slot = slotsArray.find(s => s.slot_number === parseInt(e.currentTarget.getAttribute('data-slot-number') || '0'));
 if (!slot || slot.file_hash) return;

 // 매 호출마다 highlight 보장 — child element 들어올 때 dragleave 로 사라져도
 // dragover 가 즉시 복원. drop 시점에 highlight 가 사라진 것처럼 보이는 race 방지.
 const target = e.currentTarget;
 target.style.borderColor = '#1a8fcb';
 target.style.borderWidth = '2px';
 target.style.backgroundColor = '#e8f4fb';
 };

 // dragleave 는 자식 element 진입 시에도 발생. relatedTarget 이 slot 안에
 // 머무는 경우엔 highlight 유지해 drop 일관성 확보.
 const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
 const related = e.relatedTarget as Node | null;
 if (related && e.currentTarget.contains(related)) {
 // 자식 element 로 이동 — 여전히 slot 안. highlight 유지.
 return;
 }
 e.preventDefault();
 e.stopPropagation();

 const target = e.currentTarget;
 target.style.borderColor = '';
 target.style.borderWidth = '';
 target.style.backgroundColor = '';
 };
 
 const checkIndirectAuthorization = async (authorizedFamilies: string[]): Promise<boolean> => {
 try {
 const response = await fetch(`/mofamily/${familyHash}/check-slot-authorization`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json'
 },
 credentials: 'include',
 body: JSON.stringify({ authorizedFamilies })
 });
 
 if (response.ok) {
 const data = await response.json();
 return data.data.hasAuthorization;
 }
 } catch (error) {
 }
 return false;
 };
 
 const removeVideoFromSlot = async (familyHash: string, slotNumber: number, skipConfirm: boolean = false, dropEnabled: boolean = false) => {
 if (!skipConfirm && !confirm('이 슬롯에서 영상을 제거하시겠습니까?')) {
 return;
 }
 
 const store = moFamilyStore.getState();
 const bodyData: any = { file_hash: null }; // assign API에 null을 보내서 슬롯을 비움
 
 // Multi-slot system support
 if (dropEnabled && store.editingSlotSet) {
 bodyData.set_name = store.editingSlotSet;
 }
 
 const response = await fetch(`/mofamily/${familyHash}/slot/${slotNumber}/assign`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json'
 },
 credentials: 'include',
 body: JSON.stringify(bodyData)
 });
 
 if (!response.ok) {
 const error = await response.json();
 throw new Error(error.error || 'Failed to remove video from slot');
 }
 
 // Update local state
 const updatedSlots = slotsArray.map(slot => {
 if (slot.slot_number === slotNumber) {
 return { ...slot, file_hash: null, occupied: null };
 }
 return slot;
 });
 
 // Update the store
 store.setSlots(familyHash, updatedSlots);
 };
 
 const assignVideoToSlot = async (slotNumber: number, fileHash: string) => {
 const targetSlot = slotsArray.find(s => s.slot_number === slotNumber);
 
 if (!targetSlot) {
 throw new Error('슬롯을 찾을 수 없습니다.');
 }
 
 // Determine which family hash to use for the API call
 // If viewing from hierarchy (dropEnabled=false), use currentTopFamilyHash
 // If viewing own slots (dropEnabled=true), use familyHash
 const authorizedFamilyHash = !dropEnabled && currentTopFamilyHash ? currentTopFamilyHash : familyHash;
 
 // Check if the authorized family has permission
 if (!targetSlot.authorized_families.includes(authorizedFamilyHash)) {
 const hasIndirectAuth = await checkIndirectAuthorization(targetSlot.authorized_families);
 if (!hasIndirectAuth) {
 throw new Error('이 슬롯에 영상을 할당할 권한이 없습니다.');
 }
 }
 
 // Add set_name parameter for multi-slot system
 const store = moFamilyStore.getState();
 const bodyData: any = { file_hash: fileHash };
 
 // Multi-slot system support
 // For child families (when dropEnabled is false), don't send set_name
 // This will make the server use the active set
 if (dropEnabled && store.editingSlotSet) {
 bodyData.set_name = store.editingSlotSet;
 }
 
 const response = await fetch(`/mofamily/${familyHash}/slot/${slotNumber}/assign`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json'
 },
 credentials: 'include',
 body: JSON.stringify(bodyData)
 });
 
 if (!response.ok) {
 const error = await response.json();
 throw new Error(error.error || 'Failed to assign video to slot');
 }
 
 // Get the updated slots from the response
 const responseData = await response.json();
 const updatedSlotsFromServer = responseData.data;
 
 // Ensure updatedSlotsFromServer is an array
 if (!Array.isArray(updatedSlotsFromServer)) {
 console.error('Invalid slots data from server:', updatedSlotsFromServer);
 // If server hasn't been updated yet, handle the old format
 // For now, just reload slot sets from API
 const reloadResponse = await fetch(`/mofamily/${familyHash}/slot-sets`, {
 credentials: 'include'
 });
 if (reloadResponse.ok) {
 const reloadData = await reloadResponse.json();
 const sets = reloadData.data.sets;
 store.setSlotSets(familyHash, sets);
 const targetSet = sets.find(s => s.setName === (store.editingSlotSet || 'A'));
 if (targetSet && Array.isArray(targetSet.slots)) {
 setSlots(targetSet.slots);
 }
 }
 return;
 }
 
 // Update local state for UI
 setSlots(updatedSlotsFromServer);

 // Update the store with the new slots for multi-slot system.
 // 이전엔 updatedSets 만 만들고 store.setSlotSets 호출 안 해 SlotDisplay 가
 // local state 만 갱신하고 parent 컴포넌트들의 slotSets 는 stale → 다음 render
 // 에서 stale slot 으로 덮여 영상이 안 들어간 것처럼 보이는 버그.
 const currentSets = store.slotSets.get(familyHash) || [];
 const targetSetName = dropEnabled ? (store.editingSlotSet || 'A') : 'active';

 const updatedSets = currentSets.map(set => {
 if ((targetSetName === 'active' && set.isActive) ||
 (targetSetName !== 'active' && set.setName === targetSetName)) {
 return { ...set, slots: updatedSlotsFromServer };
 }
 return set;
 });

 store.setSlotSets(familyHash, updatedSets);
 };

 // 서버에서 슬롯셋을 다시 읽어 store/local 갱신. bind 처럼 응답에 슬롯이 없는 경로용.
 const reloadSlotSets = async () => {
 const store = moFamilyStore.getState();
 const r = await fetch(`/mofamily/${familyHash}/slot-sets`, { credentials: 'include' });
 if (!r.ok) return;
 const data = await r.json();
 const sets = data.data?.sets;
 if (!Array.isArray(sets)) return;
 store.setSlotSets(familyHash, sets);
 const targetSet = sets.find((s: any) => s.setName === (store.editingSlotSet || 'A'));
 if (targetSet && Array.isArray(targetSet.slots)) setSlots(targetSet.slots);
 };

 return (
 <>
 <div className="inline-flex flex-wrap gap-1 justify-end">
 {slotsArray.length === 0 ? (
 <div className="text-gray-500 text-sm">슬롯이 없습니다.</div>
 ) : (
 slotsArray.map((slot) => {
 const slotNumber = slot.slot_number;
 
 // Determine delegation type based on authorized_families
 let delegationType: 'dedicated' | 'shared' | null = null;
 
 // For viewing from hierarchy (dropEnabled = false), check if current top family has delegation
 if (!dropEnabled && currentTopFamilyHash && slot.authorized_families.includes(currentTopFamilyHash)) {
 if (slot.authorized_families.length === 1) {
 delegationType = 'dedicated';
 } else {
 delegationType = 'shared';
 }
 } else if (dropEnabled) {
 // For viewing own slots
 // First check from loaded delegation info
 delegationType = slotDelegationInfo[slotNumber];
 
 // If no delegation info, determine based on authorized_families
 if (!delegationType) {
 if (slot.authorized_families.length === 1 && slot.authorized_families[0] === familyHash) {
 // Only this family has authority, it's effectively dedicated
 delegationType = 'dedicated';
 } else if (slot.authorized_families.length > 1 && slot.authorized_families.includes(familyHash)) {
 // Multiple families have authority including this one, it's shared
 delegationType = 'shared';
 }
 }
 }
 
 // Check if user has authority over this slot
 let hasAuthority = false;
 
 // First check if current family is viewing their own slots (dropEnabled = true)
 if (dropEnabled) {
 // Check if this is a dedicated slot where current family is the sole authority
 if (slot.authorized_families.length === 1 && slot.authorized_families[0] === familyHash) {
 // This family has dedicated authority on this slot
 hasAuthority = true;
 } else if (slot.authorized_families.includes(familyHash)) {
 // This family has shared authority on this slot
 hasAuthority = true;
 } else {
 // This family has no authority on this slot
 hasAuthority = false;
 }
 } else {
 // Viewing from hierarchy - check if current family has authority
 if (slot?.authorized_families && currentTopFamilyHash && slot.authorized_families.includes(currentTopFamilyHash)) {
 // Current family has authority on this slot
 hasAuthority = true;
 }
 }
 
 // Check if slot is occupied by another family (for shared slots)
 // When viewing from current family's perspective (dropEnabled = true)
 const viewingFamilyHash = dropEnabled ? familyHash : currentTopFamilyHash;
 const isOccupiedByOther = slot.occupied && slot.occupied !== viewingFamilyHash;
 
 // For shared slots, check if current family can remove the video
 let canRemoveVideo = hasAuthority;
 if (hasAuthority && slot.authorized_families.length > 1 && isOccupiedByOther) {
 // Shared slot occupied by another family - cannot remove
 canRemoveVideo = false;
 }
 
 // Check if can drop video to this slot
 let canDrop = false;
 // Can drop if has authority and slot is empty
 canDrop = hasAuthority && !slot?.file_hash;
 
 return (
 <div
 key={slotNumber}
 data-slot-number={slotNumber}
 className={`relative w-16 h-9 border flex items-center justify-center text-[10px] font-medium transition-all
 ${getSlotBorderColor(delegationType)}
 ${!hasAuthority ? 'bg-gray-100' : 'bg-white'}
 ${!hasAuthority ? (dropEnabled ? 'slot-delegated-away cursor-grab' : 'slot-no-authority cursor-not-allowed') : 'cursor-pointer'}
 ${slot?.file_hash ? 'hover:shadow-md' : (hasAuthority ? 'hover:bg-gray-50' : '')}`}
 onDrop={(canDrop || dropEnabled) ? (e) => handleDrop(e, slotNumber, canDrop) : undefined}
 onDragOver={(canDrop || dropEnabled) ? handleDragOver : undefined}
 onDragLeave={handleDragLeave}
 title={getSlotTooltip(delegationType, slot, hasAuthority)}
 draggable={dropEnabled && !hasAuthority}
 onDragStart={dropEnabled && !hasAuthority ? (e) => {
 // 위임(사선) 슬롯은 내용을 못 만지므로 칸 자체를 드래그해 순서를 바꾼다.
 e.dataTransfer.effectAllowed = 'move';
 e.dataTransfer.setData('application/x-slot-swap', JSON.stringify({ slotNumber }));
 } : undefined}
 >
 {boxBoundSlots.has(slotNumber) && (
 <span
 draggable={dropEnabled}
 className={`absolute top-0 left-0 z-10 bg-purple-600 text-white text-sm leading-none px-0.5 py-0.5 rounded-br ${dropEnabled ? 'cursor-grab' : 'pointer-events-none'}`}
 title={`${boxBoundSlots.get(slotNumber)?.name ?? '영상 박스'}${dropEnabled
 ? ' — 슬롯 밖으로 드래그하면 박스를 뺍니다'
 : ' — 시각에 맞는 영상이 자동 재생됩니다'}`}
 onDragStart={dropEnabled ? (e) => {
 e.stopPropagation();
 e.dataTransfer.effectAllowed = 'move';
 e.dataTransfer.setData('application/x-videobox-unbind', JSON.stringify({ slotNumber }));
 } : undefined}
 onDragEnd={dropEnabled ? (e) => {
 // 유효 드롭존(다른 슬롯) 밖에 놓으면 dropEffect==='none' → 그 때 unbind.
 // 슬롯 위 드롭은 handleDrop 이 처리하므로 여기선 중복 실행 안 함.
 if (e.dataTransfer.dropEffect === 'none') void handleUnbindBox(slotNumber);
 } : undefined}
 >{(() => { const t = boxBoundSlots.get(slotNumber)?.type; return t === 'sequence' ? '🔁' : t === 'group' ? '🗂️' : '📦'; })()}</span>
 )}
 {dropEnabled && hasAuthority && (
 <span
 draggable
 className="absolute bottom-0 right-0 z-10 text-[9px] leading-none px-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 cursor-grab rounded-tl"
 title="드래그해 다른 슬롯과 순서 바꾸기"
 onDragStart={(e) => {
 e.stopPropagation();
 e.dataTransfer.effectAllowed = 'move';
 e.dataTransfer.setData('application/x-slot-swap', JSON.stringify({ slotNumber }));
 }}
 >⠿</span>
 )}
 {slot?.file_hash ? (
 <div
 draggable={canRemoveVideo}
 className={`w-full h-full ${!canRemoveVideo ? 'cursor-not-allowed' : ''}`}
 title={!canRemoveVideo
 ? '다른 모패밀리가 넣은 영상입니다'
 : (() => {
 // ⚠️ 빈 title('') 은 부모 슬롯 툴팁까지 삼킨다 — 이름을 여기서 직접 단다.
 const b = boxBoundSlots.get(slotNumber);
 if (b) return `${b.type === 'sequence' ? '🔁' : b.type === 'group' ? '🗂️' : '📦'} ${b.name} (${b.type === 'sequence' ? '시퀀싱' : b.type === 'group' ? '그룹' : '스케줄'} 박스)`;
 return `📹 ${videoTitles.get(slot.file_hash) ?? String(slot.file_hash).slice(5, 13)}`;
 })()}
 onDragStart={canRemoveVideo ? (e) => {
 const dragData = {
 slotNumber: slotNumber,
 fileHash: slot.file_hash,
 familyHash: familyHash
 };
 e.dataTransfer.effectAllowed = 'move';
 e.dataTransfer.setData('application/x-slot-video', JSON.stringify(dragData));
 
 // Add dragging class for visual feedback
 const target = e.currentTarget as HTMLDivElement;
 target.classList.add('dragging');
 target.style.opacity = '0.3';
 
 // Store the current slot data in case we need to restore it
 e.dataTransfer.setData('text/plain', 'slot-drag');
 
 if (onDragStart) onDragStart();
 } : undefined}
 onDragEnd={canRemoveVideo ? (e) => {
 const target = e.currentTarget as HTMLDivElement;
 target.classList.remove('dragging');
 target.style.opacity = '1';
 if (onDragEnd) onDragEnd();
 } : undefined}
 >
 <ThumbnailDisplay fileHash={slot.file_hash} isLocked={!canRemoveVideo} />
 </div>
 ) : null}
 </div>
 );
 })
 )}
 </div>
 </>
 );
};

interface ThumbnailDisplayProps {
 fileHash: string;
 isLocked?: boolean;
}

// Cache for file info requests to avoid duplicate 403 errors
const fileInfoCache = new Map<string, { thumbnailPath?: string | null, status: number, expiresAt?: string | null, eventStartAt?: string | null }>();

const ThumbnailDisplay: React.FC<ThumbnailDisplayProps> = ({ fileHash, isLocked = false }) => {
 const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
 // 라이프타임 만료 영상은 playing-info 가 슬롯에서 제외(재생 안 됨) → 슬롯 썸네일을 dimmed 표시.
 const [expired, setExpired] = useState(false);
 // 만료 아니고 행사가 미래면 D-N 뱃지.
 const [dday, setDday] = useState<number | null>(null);

 useEffect(() => {
 const applyMeta = (expiresAt?: string | null, eventStartAt?: string | null) => {
 const exp = isExpired(expiresAt);
 setExpired(exp);
 setDday(exp ? null : eventDDay(eventStartAt));
 };
 const loadThumbnail = async () => {
 // Check cache first
 const cached = fileInfoCache.get(fileHash);
 if (cached) {
 if (cached.status === 403 || cached.status === 404) {
 // Already know it's inaccessible or missing — skip request
 return;
 }
 if (cached.thumbnailPath) {
 setThumbnailUrl(`/mofile/thumbnail/${cached.thumbnailPath}`);
 }
 applyMeta(cached.expiresAt, cached.eventStartAt);
 return;
 }

 try {
 const response = await fetch(`/mofile/${fileHash}/info`, {
 credentials: 'include'
 });

 if (response.ok) {
 const data = await response.json();
 const file = data.data;

 applyMeta(file?.expires_at, file?.event_start_at);
 if (file?.thumbnail_path) {
 setThumbnailUrl(`/mofile/thumbnail/${file.thumbnail_path}`);
 fileInfoCache.set(fileHash, { thumbnailPath: file.thumbnail_path, status: 200, expiresAt: file.expires_at, eventStartAt: file.event_start_at });
 } else {
 fileInfoCache.set(fileHash, { thumbnailPath: null, status: 200, expiresAt: file.expires_at, eventStartAt: file.event_start_at });
 }
 } else if (response.status === 403 || response.status === 404) {
 // Cache 403/404 to avoid spamming requests for stale slot references
 // (slot may still hold a hash for a file the current user can't access,
 // or for a file that was deleted but never cleared from the slot).
 fileInfoCache.set(fileHash, { status: response.status });
 return;
 }
 } catch (error) {
 }
 };
 
 if (fileHash) {
 loadThumbnail();
 }
 }, [fileHash]);
 
 if (thumbnailUrl) {
 return (
 <>
 <img
 src={thumbnailUrl}
 alt="Video thumbnail"
 className={`absolute inset-0 w-full h-full object-cover${expired ? ' opacity-40' : ''}`}
 />
 {expired ? (
 <span className="absolute top-0 right-0 z-10 px-1 text-[8px] leading-tight bg-gray-700 text-white rounded-bl" title="만료됨 — 재생 안 됨">만료</span>
 ) : dday != null ? (
 <span className="absolute top-0 right-0 z-10 px-1 text-[8px] leading-tight bg-blue-600 text-white rounded-bl" title="행사 시작까지 남은 일수">D-{dday}</span>
 ) : null}
 {isLocked && (
 <div className="absolute inset-0 flex items-center justify-center">
 <div className="bg-black bg-opacity-50 p-1">
 <svg className="w-4 h-4 fill-white" viewBox="0 0 20 20">
 <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
 </svg>
 </div>
 </div>
 )}
 </>
 );
 }

 // Fallback: show video icon
 return (
 <>
 <svg className={`w-full h-full fill-gray-500${expired ? ' opacity-40' : ''}`} viewBox="0 0 24 24">
 <path d="M10 8v8l5-4z"/>
 </svg>
 {expired ? (
 <span className="absolute top-0 right-0 z-10 px-1 text-[8px] leading-tight bg-gray-700 text-white rounded-bl" title="만료됨 — 재생 안 됨">만료</span>
 ) : dday != null ? (
 <span className="absolute top-0 right-0 z-10 px-1 text-[8px] leading-tight bg-blue-600 text-white rounded-bl" title="행사 시작까지 남은 일수">D-{dday}</span>
 ) : null}
 {isLocked && (
 <div className="absolute inset-0 flex items-center justify-center">
 <div className="bg-black bg-opacity-50 p-1">
 <svg className="w-4 h-4 fill-white" viewBox="0 0 20 20">
 <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
 </svg>
 </div>
 </div>
 )}
 </>
 );
};